if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get optionGroups() {
        return Array.from(this.querySelectorAll('variant-selects [data-option-role]'));
      }

      // Non-color option groups (size, width, …) the shopper hasn't picked yet.
      // A re-render always comes back with a variant selected, so "not picked"
      // can't be read off the DOM — pickedOptionPositions is the record.
      get pendingOptionGroups() {
        return this.optionGroups.filter(
          (group) =>
            group.dataset.optionRole !== 'color' && !this.pickedOptionPositions.has(group.dataset.optionPosition)
        );
      }

      get hasPendingOptions() {
        return this.pendingOptionGroups.length > 0;
      }

      // El usuario apretó Agregar sin elegir talle: el bloque de talles hace
      // shake y aparece el mensaje inline. La página no se mueve, igual que en
      // el boceto.
      requireSize() {
        this.pendingOptionGroups.forEach((group) => {
          const error = group.querySelector('[data-size-error]');
          if (error) error.hidden = false;

          group.classList.remove('is-shaking');
          // Reflow forzado para que un segundo click reinicie la animación.
          void group.offsetWidth;
          group.classList.add('is-shaking');
        });

        clearTimeout(this.shakeTimer);
        this.shakeTimer = setTimeout(() => {
          this.querySelectorAll('.is-shaking').forEach((g) => g.classList.remove('is-shaking'));
        }, 700);
      }

      clearSizeError() {
        this.querySelectorAll('[data-size-error]').forEach((error) => (error.hidden = true));
      }

      // Captura sobre product-info, un ancestro del botón, para correr antes que
      // cualquier listener del botón o del form: ProductForm registra su submit
      // en el constructor, y sobre el mismo nodo los handlers corren en orden de
      // registro aunque sean de captura. Cancelar el click cancela el submit.
      onAddButtonClick(event) {
        const button = event.target.closest?.('[name="add"]');
        if (!button || !this.hasPendingOptions) return;

        event.preventDefault();
        event.stopPropagation();
        this.requireSize();
      }

      // La barra sticky pide el shake, porque el bloque de talles vive acá.
      onSizeRequired(event) {
        if (event.detail && event.detail.sectionId && event.detail.sectionId !== this.sectionId) return;
        if (!this.hasPendingOptions) return;
        this.requireSize();
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.initPendingOptions();

        this.onAddButtonClick = this.onAddButtonClick.bind(this);
        this.addEventListener('click', this.onAddButtonClick, true);

        this.onSizeRequired = this.onSizeRequired.bind(this);
        document.addEventListener('size:required', this.onSizeRequired);

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
      }

      // Entering a PDP must not pre-select a size: the shopper picks it. The
      // `variant` URL parameter is not an explicit size choice — it is how a
      // product card carries its colour across, since Shopify has no colour-only
      // parameter — so its colour applies but its size is dropped. A size the
      // shopper did pick is remembered on the history entry (see updateURL), which
      // is what survives a reload. Outside the PDP (quick view, featured product)
      // nothing changes.
      initPendingOptions() {
        this.pickedOptionPositions = new Set();

        const stamped = this.dataset.optionsPending;
        const pending =
          stamped === 'true' ||
          (stamped === undefined && this.dataset.updateUrl === 'true' && window.history.state?.sizePicked !== true);

        if (!pending) {
          this.optionGroups.forEach((group) => this.pickedOptionPositions.add(group.dataset.optionPosition));
        }

        this.applyPendingOptionState();
      }

      // Hold the picker, the form's variant id and the submit button in the
      // "no size picked yet" state. Re-applied after every re-render, since the
      // server always answers with a variant selected.
      applyPendingOptionState() {
        const groups = this.pendingOptionGroups;
        this.dataset.optionsPending = groups.length ? 'true' : 'false';
        if (!groups.length) return;

        groups.forEach((group) => {
          group.querySelectorAll('input:checked').forEach((input) => (input.checked = false));

          // El re-render trae en la leyenda el talle que resolvió Shopify (que
          // puede ser uno oculto por `custom.hide_variant`); mientras no haya
          // talle elegido tiene que decir "Seleccioná un talle".
          const label = group.querySelector('[data-selected-swatch-value]');
          if (label && label.dataset.placeholder) label.textContent = label.dataset.placeholder;

          const select = group.querySelector('select');
          if (select) this.resetSelectToPlaceholder(select);
        });

        this.updateVariantInputs(null);
      }

      // Un talle ya elegido puede no volver en el re-render: en el color nuevo
      // está oculto por `custom.hide_variant` y product-variant-options no lo
      // dibuja. Vuelve a quedar pendiente; si no, el form iría con la variante
      // de reemplazo sin ningún talle marcado. Se mira el picker nuevo: el viejo
      // sigue en el DOM medio segundo (HTMLUpdateUtility.viewTransition).
      releaseMissingOptions() {
        const picker = this.variantSelectors;
        if (!picker) return;

        picker.querySelectorAll('[data-option-role="other"]').forEach((group) => {
          const position = group.dataset.optionPosition;
          if (!this.pickedOptionPositions.has(position)) return;

          const picked = group.querySelector('input:checked, select option[selected][data-option-value-id]');
          if (!picked) this.pickedOptionPositions.delete(position);
        });
      }

      // The placeholder keeps the `selected` attribute, not just the property:
      // variant-selects reads the attribute to find the current option, and it
      // carries no option-value id, so nothing is sent for this option.
      resetSelectToPlaceholder(select) {
        let placeholder = select.querySelector('option[data-option-placeholder]');

        if (!placeholder) {
          placeholder = new Option(select.dataset.placeholder || '', '');
          placeholder.disabled = true;
          placeholder.dataset.optionPlaceholder = '';
          select.prepend(placeholder);
        }

        Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
        placeholder.setAttribute('selected', 'selected');
        placeholder.selected = true;
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.removeEventListener('click', this.onAddButtonClick, true);
        document.removeEventListener('size:required', this.onSizeRequired);
        clearTimeout(this.shakeTimer);
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        // The shopper touched this option group, so it stops being pending.
        const changedGroup = event.target.closest('[data-option-role]');
        if (changedGroup) this.pickedOptionPositions.add(changedGroup.dataset.optionPosition);
        this.dataset.optionsPending = this.hasPendingOptions ? 'true' : 'false';
        this.clearSizeError();

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          let previousVariant = this.getSelectedVariant(this)

          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.releaseMissingOptions();
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            this.selectAvailableVariantIfExists(previousVariant)
            return;
          }

          if(!!this.querySelector('media-gallery')?.dataset.hideVariantMedia) {
            if((!!previousVariant && previousVariant.option1 !== variant.option1) || !!!previousVariant) {
              let mediaContainer = this.querySelector('.product__media-container')
              let newMediaContainer = html.querySelector('.product__media-container')
              if(!!mediaContainer && !!newMediaContainer) mediaContainer.innerHTML = newMediaContainer.innerHTML
            }
            
          }else {
            this.updateMedia(variant);
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );

          // After toggleAddButton, so the "pick a size" state wins over the
          // enabled button the fresh markup came back with.
          this.applyPendingOptionState();

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      selectAvailableVariantIfExists(previousVariant) {
        if (!previousVariant || !previousVariant.option3) return
        let inputs = this.querySelectorAll(`variant-selects fieldset + fieldset + fieldset input`)
        Array.from(inputs).find(input => input.value !== previousVariant.option3)?.click()
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      // The state records whether the shopper has settled the size on this history
      // entry. A reload reuses the entry and restores its state, so their choice
      // survives; arriving from a product card is a fresh navigation with no state,
      // which is what keeps the size unselected on entry.
      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState(
          { sizePicked: !this.hasPendingOptions },
          '',
          `${url}${variantId ? `?variant=${variantId}` : ''}`
        );
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        if(!!this.querySelector('media-gallery')?.dataset.hideVariantMedia) {
          return
        }

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}
